"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAccountStatistics, useAccountTier } from "@/lib/api/accounts";
import { useCasinoBets, useRewardTransactions, useTransactions } from "@/lib/api/hooks";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { useModalChrome } from "@/components/ui/Modal";
import { Icon } from "@/components/ui/Icon";
import { BetRows, TransferRows } from "./LiveStats";
import { TierLine } from "./ProfileCard";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";
import { ProfileBanner } from "@/components/profile/ProfileBanner";

/* A player's statistics, the way the whitelabel's StatsModal draws them: an
   overview — name, join date, the three lifetime totals — and behind each
   total an "advanced" view listing that player's own bets, transactions or
   rewards, with tabs to move between them and an arrow back to the overview.

   The lists are the same rows as the live feeds, filtered to one account by
   the public API (`?accountId=`), read once rather than polled. Opened from
   the player card by clicking a total; rendered through a portal, because the
   card sits inside an animated popover whose transform would otherwise trap a
   fixed-position dialog. */

export type StatsTab = "overview" | "bets" | "transactions" | "rewards";

const ADVANCED: { tab: Exclude<StatsTab, "overview">; label: string }[] = [
  { tab: "bets", label: "Bets" },
  { tab: "transactions", label: "Transactions" },
  { tab: "rewards", label: "Rewards" },
];

export function PlayerStatsModal({
  accountId,
  name,
  initialTab = "overview",
  onClose,
}: {
  accountId: string | null;
  name: string;
  initialTab?: StatsTab;
  onClose: () => void;
}) {
  const open = accountId !== null;
  const panel = useModalChrome(open, onClose);
  const [tab, setTab] = useState<StatsTab>(initialTab);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && accountId && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={`${name}'s statistics`}
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className={cn(
              "modal-shadow relative flex max-h-[90vh] w-full flex-col gap-[16px] overflow-y-auto rounded-2xl border border-border-input bg-elevation-1 p-[24px]",
              tab === "overview" ? "max-w-[440px]" : "max-w-[960px]",
            )}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close statistics"
              className="absolute right-[16px] top-[16px] grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
            >
              <Icon name="close" size={18} />
            </button>

            {tab === "overview" ? (
              <Overview accountId={accountId} name={name} onPick={setTab} />
            ) : (
              <Advanced accountId={accountId} name={name} tab={tab} onTab={setTab} />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function Overview({
  accountId,
  name,
  onPick,
}: {
  accountId: string;
  name: string;
  onPick: (tab: StatsTab) => void;
}) {
  const statistics = useAccountStatistics(accountId, true);
  const tier = useAccountTier(accountId, statistics.data);
  const joined = statistics.data
    ? new Date(statistics.data.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;
  const loading = statistics.isPending;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col gap-[20px]"
    >
      <ProfileBanner as="div" accountId={accountId} className="-mx-[4px] rounded-lg">
        <div className="flex items-center gap-[12px] py-[12px] pl-[4px] pr-[32px]">
          <ProfileAvatar accountId={accountId} name={name} size={48} glow />
          <div className="flex min-w-0 flex-col">
            <h2 className="truncate text-[17.5px] font-bold leading-[1.4] text-fg">{statistics.data?.nickname || name}</h2>
            <TierLine tier={tier} joined={joined} />
          </div>
        </div>
      </ProfileBanner>

      <div className="grid grid-cols-1 gap-[8px] sm:grid-cols-3">
        <StatItem label="Total wagered" value={statistics.data?.wagered} loading={loading} onClick={() => onPick("bets")} />
        <StatItem label="Total bets" value={statistics.data ? String(statistics.data.bets) : undefined} loading={loading} onClick={() => onPick("bets")} />
        <StatItem label="Total transactions" value={statistics.data ? String(statistics.data.transactions) : undefined} loading={loading} onClick={() => onPick("transactions")} />
      </div>

      <p className="text-center text-[11px] leading-[1.6] text-fg-subtle">
        Click a total to see the bets, transactions or rewards behind it.
      </p>
    </motion.div>
  );
}

/* A total on the overview: a button, because each one opens the list behind
   it, and it looks like one — pointer, hover, the kit's chevron. */
function StatItem({
  label,
  value,
  loading,
  onClick,
}: {
  label: string;
  value?: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="group flex cursor-pointer flex-col items-start gap-[4px] rounded-lg border border-border bg-elevation-2 px-[14px] py-[12px] text-left transition-colors hover:border-brand hover:bg-elevation-3 disabled:cursor-progress"
    >
      <span className="text-[9px] font-bold uppercase tracking-wide text-fg-subtle">{label}</span>
      <span className="flex w-full items-center justify-between gap-[8px]">
        {loading ? (
          <span className="h-[20px] w-[80px] animate-pulse rounded bg-elevation-3" />
        ) : (
          <span className="text-[16px] font-bold text-fg">{value ?? "—"}</span>
        )}
        <Icon name="chevron-right" size={14} className="shrink-0 text-fg-subtle transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function Advanced({
  accountId,
  name,
  tab,
  onTab,
}: {
  accountId: string;
  name: string;
  tab: StatsTab;
  onTab: (tab: StatsTab) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col gap-[16px]"
    >
      <div className="flex items-center gap-[10px] pr-[32px]">
        <button
          type="button"
          onClick={() => onTab("overview")}
          aria-label="Back to overview"
          className="grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-md bg-elevation-3 text-fg-subtle transition-colors hover:bg-elevation-4 hover:text-fg"
        >
          <Icon name="chevron-left" size={16} />
        </button>
        <div className="flex min-w-0 flex-col">
          <h2 className="text-[17.5px] font-bold leading-[1.4] text-fg">Advanced stats</h2>
          <p className="truncate text-[12px] leading-[1.6] text-fg-subtle">{name}</p>
        </div>
      </div>

      <div className="flex gap-[4px] rounded-lg bg-elevation-2 p-[4px]">
        {ADVANCED.map((entry) => (
          <button
            key={entry.tab}
            type="button"
            onClick={() => onTab(entry.tab)}
            className={cn(
              "flex h-[32px] flex-1 cursor-pointer items-center justify-center rounded-md text-[12px] font-bold transition-colors",
              tab === entry.tab ? "bg-brand text-fg-on-primary" : "text-fg-subtle hover:bg-elevation-3 hover:text-fg",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "bets" && <PlayerBets accountId={accountId} />}
      {tab === "transactions" && <PlayerTransactions accountId={accountId} />}
      {tab === "rewards" && <PlayerRewards accountId={accountId} />}
    </motion.div>
  );
}

function PlayerBets({ accountId }: { accountId: string }) {
  const feed = useCasinoBets({ accountId, limit: 25 });

  if (feed.isPending) {
    return <div className="h-[180px] w-full animate-pulse rounded-xl bg-elevation-2" />;
  }

  return <BetRows bets={feed.bets} />;
}

function PlayerTransactions({ accountId }: { accountId: string }) {
  const feed = useTransactions({ accountId, limit: 25 });

  return (
    <TransferRows
      kindLabel="TYPE"
      feed={feed}
      emptyTitle="No transactions yet"
      emptyBody="This player's deposits and withdrawals appear here as they settle."
      errorTitle="The transaction history is unavailable"
      highlightIncoming={false}
    />
  );
}

function PlayerRewards({ accountId }: { accountId: string }) {
  const feed = useRewardTransactions({ accountId, limit: 25 });

  return (
    <TransferRows
      kindLabel="REWARD"
      feed={feed}
      emptyTitle="No rewards paid out yet"
      emptyBody="This player's rakeback, cashback and bonus payouts appear here as they are claimed."
      errorTitle="The reward history is unavailable"
    />
  );
}
